import * as z from "zod/v4";

/**
 * RESOLVE (internally "deflection") request storage. Disputely's RESOLVE product
 * fires a synchronous `resolve.order.requested` webhook when an issuer queries us
 * about a transaction (the cardholder is on the phone with their issuer). We must
 * answer within 3s with the matched order detail; this table is the audit log of
 * every inquiry we received, our verbatim response, and how we classified it —
 * the partner-side mirror of Disputely's "resolve lookup" record (§5.7.1).
 *
 * Mirrors `dispute_alert` (same account-scoped, soft-delete, raw-payload shape).
 * The row is written fire-and-forget AFTER we respond, so it never sits on the
 * 3-second critical path. `outcome` is a free-form STRING column (the
 * {@link DisputeDeflectionOutcome} union documents the values we emit), mirroring
 * `dispute_alert.decision_rule`'s unconstrained-string approach — no CRDB enum.
 */

/**
 * Our classification of how we answered an inquiry. Mirrors Disputely's resolve
 * lookup `outcome` (§5.7.3): `match` (returned a `found` order), `no_match`
 * (`not_found`), `multiple` (`multiple`), plus the failure modes we record when
 * we could not answer cleanly.
 */
export const disputeDeflectionOutcome = [
  "match",
  "no_match",
  "multiple",
  "timeout",
  "schema_error",
  "partner_error",
] as const;

export type DisputeDeflectionOutcome =
  (typeof disputeDeflectionOutcome)[number];

/**
 * Columns the deflection list views (merchant tab + admin tab) can sort by. Both
 * map to indexed columns on `dispute_deflection`. Mirrors
 * `disputeAlertSortableColumns`.
 */
export const disputeDeflectionSortableColumns = [
  "receivedAt",
  "createdAt",
] as const;

export type DisputeDeflectionSortableColumn =
  (typeof disputeDeflectionSortableColumns)[number];

const disputeDeflectionSchema_ = z.object({
  // Our row's primary key (CRDB random_id), not Disputely's.
  id: z.coerce.bigint(),
  // OUR CRDB account.id (FK to `account`), resolved from the Disputely account
  // id at request time. Not the Disputely account id.
  accountId: z.coerce.bigint(),
  // Disputely's delivery id (the `Disputely-Delivery-Id` header / resolve-lookup
  // `id`); the per-account dedupe key.
  sourceDeliveryId: z.string(),
  // --- inquiry context (§5.6.1 inquiry.*) ---
  correlationId: z.string().nullish(),
  issuerName: z.string().nullish(),
  issuerId: z.string().nullish(),
  sourceId: z.string().nullish(),
  originatorChannel: z.string().nullish(),
  transactionCategory: z.string().nullish(),
  locale: z.string().nullish(),
  // --- dispute context (§5.6.1 inquiry.disputeContext.*) ---
  disputeId: z.string().nullish(),
  disputeIdType: z.string().nullish(),
  // --- transaction context (§5.6.1 transaction.*) ---
  paymentType: z.string().nullish(),
  authCode: z.string().nullish(),
  acquirerReferenceNumber: z.string().nullish(),
  transactionAmountMinor: z.coerce.number().nullish(),
  transactionCurrency: z.string().nullish(),
  transactionDate: z.coerce.date().nullish(),
  cardBin: z.string().nullish(),
  cardLastFour: z.string().nullish(),
  merchantId: z.string().nullish(),
  transactionIdentifierType: z.string().nullish(),
  transactionIdentifierValue: z.string().nullish(),
  cardAcceptorId: z.string().nullish(),
  cardAcceptorName: z.string().nullish(),
  cardAcceptorLocation: z.string().nullish(),
  cardAcceptorRegion: z.string().nullish(),
  cardAcceptorCountry: z.string().nullish(),
  clearingAmountMinor: z.coerce.number().nullish(),
  clearingCurrency: z.string().nullish(),
  clearingDate: z.coerce.date().nullish(),
  // --- our response ---
  outcome: z.enum(disputeDeflectionOutcome),
  // The Redo order (Mongo `_id`, 24-char hex; stored as BYTES and encode/decoded
  // by the repo at the SQL boundary) we matched and responded with, when outcome
  // is `match`. Gives the detail page an indexed Order.findById lookup.
  matchedOrderId: z.string().nullish(),
  // Wall-clock ms from receivedAt to respondedAt (our latency vs. the 3s budget).
  responseTimeMs: z.coerce.number().nullish(),
  receivedAt: z.coerce.date(),
  respondedAt: z.coerce.date().nullish(),
  // Verbatim inbound webhook body and our verbatim response body.
  rawRequest: z.unknown(),
  responseBody: z.unknown().nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullish(),
});
export const disputeDeflectionSchema: z.ZodType<DisputeDeflection> =
  disputeDeflectionSchema_;
export interface DisputeDeflection extends z.infer<
  typeof disputeDeflectionSchema_
> {}

export { disputeDeflectionSchema_ };

export interface InsertDisputeDeflectionInput {
  /** OUR CRDB account.id (resolved from the Disputely account id), not Disputely's. */
  accountId: bigint;
  /** Disputely's delivery id (the `Disputely-Delivery-Id` header). */
  sourceDeliveryId: string;
  correlationId?: string | null;
  issuerName?: string | null;
  issuerId?: string | null;
  sourceId?: string | null;
  originatorChannel?: string | null;
  transactionCategory?: string | null;
  locale?: string | null;
  disputeId?: string | null;
  disputeIdType?: string | null;
  paymentType?: string | null;
  authCode?: string | null;
  acquirerReferenceNumber?: string | null;
  transactionAmountMinor?: number | null;
  transactionCurrency?: string | null;
  transactionDate?: Date | null;
  cardBin?: string | null;
  cardLastFour?: string | null;
  merchantId?: string | null;
  transactionIdentifierType?: string | null;
  transactionIdentifierValue?: string | null;
  cardAcceptorId?: string | null;
  cardAcceptorName?: string | null;
  cardAcceptorLocation?: string | null;
  cardAcceptorRegion?: string | null;
  cardAcceptorCountry?: string | null;
  clearingAmountMinor?: number | null;
  clearingCurrency?: string | null;
  clearingDate?: Date | null;
  outcome: DisputeDeflectionOutcome;
  /** Mongo order `_id` as 24-char hex (stored BYTES; the repo encode/decodes). */
  matchedOrderId?: string | null;
  responseTimeMs?: number | null;
  receivedAt: Date;
  respondedAt?: Date | null;
  rawRequest: unknown;
  responseBody?: unknown;
}
